import { TimezoneResolver } from '@principle-theorem/principle-core';
import {
  ICustomReport,
  IMarketingCampaign,
  MarketingCampaignScope,
  ReportBuilderDataSourceId,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import { IScopeDataAccessor } from '@principle-theorem/reporting/interfaces';
import { Timezone, WithRef } from '@principle-theorem/shared';
import { AccountCreditsDataSource } from '../data-sources/data-source-instances/account-credits-data-source';
import { AppointmentServiceCodesDataSource } from '../data-sources/data-source-instances/appointment-service-codes-data-source';
import { AppointmentTreatmentsDataSource } from '../data-sources/data-source-instances/appointment-treatments-data-source';
import { AppointmentsDataSource } from '../data-sources/data-source-instances/appointments-data-source';
import { InvoicesDataSource } from '../data-sources/data-source-instances/invoices-data-source';
import { PatientInteractionsDataSource } from '../data-sources/data-source-instances/patient-interactions-data-source';
import { PatientsDataSource } from '../data-sources/data-source-instances/patients-data-source';
import { PractitionerIncomeDataSource } from '../data-sources/data-source-instances/practitioner-income-data-source';
import { SchedulingEventsDataSource } from '../data-sources/data-source-instances/scheduling-events-data-source';
import { TransactionsDataSource } from '../data-sources/data-source-instances/transactions-data-source';
import { ReportBuilderDataSourceInstance } from '../data-sources/report-builder-data-source-instance';
import {
  REPORT_BUILDER_DATA_SOURCES,
  resolveDataSource,
} from '../data-sources/report-builder-data-sources';
import { PatientDimensionMeasures } from '../models/dimensions/patient-dimension';

export const SCOPE_DEFAULT_DATASOURCE_MAP: {
  [key in MarketingCampaignScope]: ReportBuilderDataSourceId;
} = {
  [TemplateScope.Patient]: ReportBuilderDataSourceId.Patients,
  [TemplateScope.Appointment]: ReportBuilderDataSourceId.Appointments,
  [TemplateScope.Invoice]: ReportBuilderDataSourceId.Invoices,
};

export interface ICampaignEntryReportContext {
  dataSource: ReportBuilderDataSourceInstance;
  patientMeasures: PatientDimensionMeasures;
  scopeDataAccessor: IScopeDataAccessor;
  scope: MarketingCampaignScope;
  timezone: Timezone;
}

export interface ICampaignEntryCSVContext {
  scope: MarketingCampaignScope;
  timezone: Timezone;
}

export class CampaignEntryReportContext {
  static build(
    scope: MarketingCampaignScope,
    timezone: Timezone,
    customDataSource?: ReportBuilderDataSourceInstance
  ): ICampaignEntryReportContext | undefined {
    const dataSource = customDataSource ?? findDefaultDatasource(scope);
    if (!dataSource) {
      return;
    }

    const patientMeasures = resolvePatientMeasures(dataSource);
    const scopeDataAccessor = dataSource.scopeDataAccessor[scope];
    if (!patientMeasures || !scopeDataAccessor) {
      return;
    }

    return {
      scope,
      dataSource,
      patientMeasures,
      scopeDataAccessor,
      timezone,
    };
  }

  static async buildFromCustomReport(
    campaign: WithRef<IMarketingCampaign>,
    customReport: WithRef<ICustomReport>
  ): Promise<ICampaignEntryReportContext | undefined> {
    const dataSource = resolveDataSource(
      customReport.dataSource,
      REPORT_BUILDER_DATA_SOURCES
    );
    const timezone = await TimezoneResolver.fromBrandRef(campaign.brandRef);
    return CampaignEntryReportContext.build(
      campaign.scope,
      timezone,
      dataSource
    );
  }
}

function findDefaultDatasource(
  scope: MarketingCampaignScope
): ReportBuilderDataSourceInstance | undefined {
  const dataSourceId = SCOPE_DEFAULT_DATASOURCE_MAP[scope];
  return resolveDataSource(dataSourceId, REPORT_BUILDER_DATA_SOURCES);
}

function resolvePatientMeasures(
  datasource: ReportBuilderDataSourceInstance
): PatientDimensionMeasures | undefined {
  if (datasource instanceof AccountCreditsDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof AppointmentsDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof AppointmentServiceCodesDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof AppointmentTreatmentsDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof InvoicesDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof PatientsDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof PatientInteractionsDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof PractitionerIncomeDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof TransactionsDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
  if (datasource instanceof SchedulingEventsDataSource) {
    return datasource.factTable.latestEvent.patient;
  }
}
